import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Check, Copy, Loader2, Plus, Trash2 } from "lucide-react";
import {
  CAMPAIGN_PRESETS,
  campaignShareUrl,
  deleteShareVariant,
  loadShareVariants,
  upsertShareVariant,
  type Campaign,
  type ShareVariant,
} from "@/lib/shareCampaigns";

type Draft = {
  variant_key: string;
  og_title: string;
  og_description: string;
  og_image_url: string;
  weight: number;
  is_active: boolean;
};

const emptyDraft = (key: string): Draft => ({
  variant_key: key,
  og_title: "",
  og_description: "",
  og_image_url: "",
  weight: 50,
  is_active: true,
});

/** Campaign (UTM) link builder + social-preview A/B variants for one share link. */
export function ShareCampaignPanel({ slug }: { slug: string }) {
  const [campaign, setCampaign] = useState<Campaign>({});
  const [copied, setCopied] = useState(false);
  const [variants, setVariants] = useState<ShareVariant[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = () =>
    loadShareVariants(slug)
      .then(setVariants)
      .catch((e) => toast.error((e as Error).message));

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const link = campaignShareUrl(slug, campaign);

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Campaign link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await upsertShareVariant(slug, {
        variant_key: draft.variant_key,
        og_title: draft.og_title || null,
        og_description: draft.og_description || null,
        og_image_url: draft.og_image_url || null,
        weight: draft.weight,
        is_active: draft.is_active,
      });
      toast.success(`Variant ${draft.variant_key.toUpperCase()} saved`);
      setDraft(null);
      await reload();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (v: ShareVariant, active: boolean) => {
    try {
      await upsertShareVariant(slug, { ...v, is_active: active });
      await reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const remove = async (v: ShareVariant) => {
    try {
      await deleteShareVariant(v.id);
      await reload();
      toast.success(`Variant ${v.variant_key} removed`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const nextKey = () => {
    const used = new Set((variants ?? []).map((v) => v.variant_key));
    for (const k of ["A", "B", "C", "D", "E"]) if (!used.has(k)) return k;
    return `V${(variants?.length ?? 0) + 1}`;
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h3 className="font-mono text-[10px] uppercase text-muted-foreground">campaign link</h3>
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              ["utm_source", "Source (e.g. twitter)"],
              ["utm_medium", "Medium (e.g. social)"],
              ["utm_campaign", "Campaign (e.g. launch)"],
              ["utm_content", "Content (e.g. thread-1)"],
            ] as const
          ).map(([key, label]) => (
            <div key={key} className="space-y-1">
              <Label htmlFor={`c-${key}`} className="text-xs">
                {label}
              </Label>
              <Input
                id={`c-${key}`}
                value={campaign[key] ?? ""}
                onChange={(e) => setCampaign((p) => ({ ...p, [key]: e.target.value }))}
              />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {CAMPAIGN_PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => setCampaign((prev) => ({ ...prev, ...p.value }))}
              className="rounded-md border border-border px-2 py-1 font-mono text-[10px] uppercase text-muted-foreground transition hover:text-foreground"
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input readOnly value={link} aria-label="Campaign share link" />
          <Button variant="outline" onClick={copy} aria-label="Copy campaign link">
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Views on this link are attributed to the channel in your dashboard, so you can see which
          source drives purchases.
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-mono text-[10px] uppercase text-muted-foreground">
            social preview A/B test
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setDraft(emptyDraft(nextKey()))}
            disabled={!!draft}
          >
            <Plus className="size-4" /> Add variant
          </Button>
        </div>

        {variants === null ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading variants…
          </p>
        ) : variants.length === 0 && !draft ? (
          <p className="text-sm text-muted-foreground">
            No variants yet. Add two with different titles or images and traffic splits between them
            automatically.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {variants.map((v) => (
              <li key={v.id} className="p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm">
                    {v.variant_key}
                    <span className="ml-2 font-mono text-[10px] text-muted-foreground">
                      weight {v.weight}
                    </span>
                  </span>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={v.is_active}
                      onCheckedChange={(c) => toggle(v, c)}
                      aria-label={`Variant ${v.variant_key} active`}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => remove(v)}
                      aria-label={`Delete variant ${v.variant_key}`}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm">{v.og_title || "(uses the default title)"}</p>
                {v.og_description && (
                  <p className="text-xs text-muted-foreground">{v.og_description}</p>
                )}
                <button
                  className="font-mono text-[10px] uppercase text-primary"
                  onClick={() =>
                    navigator.clipboard
                      .writeText(campaignShareUrl(slug, { ...campaign, variant_key: v.variant_key }))
                      .then(() => toast.success(`Link for ${v.variant_key} copied`))
                  }
                >
                  copy forced link
                </button>
              </li>
            ))}
          </ul>
        )}

        {draft && (
          <div className="rounded-lg border border-border p-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="v-key" className="text-xs">
                  Variant key
                </Label>
                <Input
                  id="v-key"
                  maxLength={24}
                  value={draft.variant_key}
                  onChange={(e) => setDraft({ ...draft, variant_key: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="v-weight" className="text-xs">
                  Traffic weight
                </Label>
                <Input
                  id="v-weight"
                  type="number"
                  min={0}
                  max={100}
                  value={draft.weight}
                  onChange={(e) => setDraft({ ...draft, weight: Number(e.target.value) })}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="v-title" className="text-xs">
                Preview title
              </Label>
              <Input
                id="v-title"
                maxLength={90}
                value={draft.og_title}
                onChange={(e) => setDraft({ ...draft, og_title: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="v-desc" className="text-xs">
                Preview description
              </Label>
              <Textarea
                id="v-desc"
                maxLength={200}
                rows={2}
                value={draft.og_description}
                onChange={(e) => setDraft({ ...draft, og_description: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="v-img" className="text-xs">
                Preview image URL (https)
              </Label>
              <Input
                id="v-img"
                placeholder="https://…"
                value={draft.og_image_url}
                onChange={(e) => setDraft({ ...draft, og_image_url: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={busy}>
                {busy && <Loader2 className="size-4 animate-spin" />} Save variant
              </Button>
              <Button variant="ghost" onClick={() => setDraft(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
